import type { Metadata } from "next";
import { baseURL } from "@/data/config";
import { clizsec, externalProfiles, knowsAbout, nameVariants, scamatrix } from "@/data/discoverability";
import { creator, product } from "@/data/content";
import {
    buildLocalizedPath,
    DEFAULT_LANGUAGE,
    OPEN_GRAPH_LOCALES,
    SUPPORTED_LANGUAGES,
    type Language,
} from "@/lib/i18n";
import { getTranslations } from "@/lib/translations";
import { buildSegmentDocumentTitle } from "@/lib/document-title";
import { buildStaticShareImagePath, SHARE_IMAGE } from "@/lib/share-image-config";

const FALLBACK_SITE_LAST_MODIFIED = "2026-10-01";

function resolveSiteLastModified(): string {
    const explicit = process.env.SITE_LAST_MODIFIED?.trim();
    if (explicit && /^\d{4}-\d{2}-\d{2}$/.test(explicit)) {
        return explicit;
    }

    const vercelCommitDate = process.env.VERCEL_GIT_COMMIT_AUTHOR_DATE?.trim();
    if (vercelCommitDate) {
        return vercelCommitDate.slice(0, 10);
    }

    return FALLBACK_SITE_LAST_MODIFIED;
}

export const SITE_LAST_MODIFIED = resolveSiteLastModified();

export const profileLinks = externalProfiles.map((profile) => profile.url);

export const shareImage = {
    ...SHARE_IMAGE,
    alt: `${product.name} - ${product.tagline}`,
};

export const SITE_URL = baseURL;
export const SITE_NAME = product.name;
export const SEO_TAGLINE = product.tagline;
export const DEFAULT_DESCRIPTION = scamatrix.description;
export const TWITTER_HANDLE = product.twitterHandle;
export const SUPPORT_EMAIL = product.supportEmail;
export const SHARED_KEYWORDS = [...nameVariants, ...knowsAbout];

export function buildAbsoluteUrl(path: string = "/"): string {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${baseURL}${normalizedPath === "/" ? "" : normalizedPath}`;
}

export function buildLanguageAlternates(
    path: string,
    defaultLanguage: Language = DEFAULT_LANGUAGE,
): Record<string, string> {
    return SUPPORTED_LANGUAGES.reduce<Record<string, string>>(
        (acc, lang) => {
            acc[lang] = buildAbsoluteUrl(buildLocalizedPath(path, lang));
            return acc;
        },
        {
            "x-default": buildAbsoluteUrl(buildLocalizedPath(path, defaultLanguage)),
        },
    );
}

export function buildSeoTitle(title?: string, tagline: string = SEO_TAGLINE): string {
    const trimmedTitle = title?.trim();

    if (!trimmedTitle) {
        return `${SITE_NAME} | ${SEO_TAGLINE}`;
    }

    return `${SITE_NAME} | ${trimmedTitle} - ${tagline}`;
}

export function buildPageMetadata(lang: Language): Metadata {
    const messages = getTranslations(lang);
    const seoTitle = messages.meta.seoTitle;
    const description = messages.meta.siteDescription;
    const shareDescription = messages.meta.shareDescription;
    const canonicalPath = buildLocalizedPath("/", lang);
    const shareAlt = `${messages.meta.siteName} - ${messages.home.heroSubtitle}`;
    const shareImageUrl = buildAbsoluteUrl(buildStaticShareImagePath(lang));

    return {
        metadataBase: new URL(baseURL),
        title: {
            default: seoTitle,
            template: `%s | ${messages.meta.siteName}`,
        },
        description,
        keywords: Array.from(new Set([...SHARED_KEYWORDS, ...messages.seo.keywords.tools])),
        authors: [{ name: creator.name, url: creator.url }],
        creator: creator.name,
        publisher: creator.name,
        applicationName: messages.meta.siteName,
        category: "technology",
        alternates: {
            canonical: canonicalPath,
            languages: buildLanguageAlternates("/"),
        },
        robots: {
            index: true,
            follow: true,
            googleBot: {
                index: true,
                follow: true,
                "max-image-preview": "large",
                "max-snippet": -1,
                "max-video-preview": -1,
            },
        },
        openGraph: {
            type: "website",
            locale: OPEN_GRAPH_LOCALES[lang],
            alternateLocale: SUPPORTED_LANGUAGES.filter((code) => code !== lang).map(
                (code) => OPEN_GRAPH_LOCALES[code],
            ),
            url: buildAbsoluteUrl(canonicalPath),
            siteName: messages.meta.siteName,
            title: seoTitle,
            description: shareDescription,
            images: [
                {
                    url: shareImageUrl,
                    width: shareImage.width,
                    height: shareImage.height,
                    alt: shareAlt,
                    type: shareImage.type,
                },
            ],
        },
        twitter: {
            card: "summary_large_image",
            title: seoTitle,
            description: shareDescription,
            creator: TWITTER_HANDLE,
            images: {
                url: shareImageUrl,
                alt: shareAlt,
            },
        },
        icons: {
            icon: [
                { url: "/favicon.ico", sizes: "any" },
                { url: "/favicon-16x16.png", sizes: "16x16", type: "image/png" },
                { url: "/favicon-32x32.png", sizes: "32x32", type: "image/png" },
            ],
            apple: [{ url: "/apple-touch-icon.png", sizes: "180x180", type: "image/png" }],
        },
    };
}

export function buildSegmentPageMetadata({
    lang,
    path,
    segmentTitle,
    description,
    keywords = [],
}: {
    lang: Language;
    path: string;
    segmentTitle: string;
    description: string;
    keywords?: string[];
}): Metadata {
    const messages = getTranslations(lang);
    const pageTitle = buildSegmentDocumentTitle(segmentTitle, messages.meta.siteName);
    const canonicalPath = buildLocalizedPath(path, lang);
    const shareAlt = `${segmentTitle} - ${messages.meta.siteName}`;
    const shareImageUrl = buildAbsoluteUrl(buildStaticShareImagePath(lang));

    return {
        title: {
            absolute: pageTitle,
        },
        description,
        keywords: Array.from(new Set([...SHARED_KEYWORDS, ...keywords])),
        alternates: {
            canonical: canonicalPath,
            languages: buildLanguageAlternates(path),
        },
        robots: {
            index: true,
            follow: true,
        },
        openGraph: {
            type: "website",
            locale: OPEN_GRAPH_LOCALES[lang],
            alternateLocale: SUPPORTED_LANGUAGES.filter((code) => code !== lang).map(
                (code) => OPEN_GRAPH_LOCALES[code],
            ),
            url: buildAbsoluteUrl(canonicalPath),
            siteName: messages.meta.siteName,
            title: pageTitle,
            description,
            images: [
                {
                    url: shareImageUrl,
                    width: shareImage.width,
                    height: shareImage.height,
                    alt: shareAlt,
                    type: shareImage.type,
                },
            ],
        },
        twitter: {
            card: "summary_large_image",
            title: pageTitle,
            description,
            creator: TWITTER_HANDLE,
            images: {
                url: shareImageUrl,
                alt: shareAlt,
            },
        },
    };
}

/** @deprecated Use buildSegmentPageMetadata */
export function createPageMetadata({
    title,
    description = DEFAULT_DESCRIPTION,
    path = "/",
    keywords = [],
    lang = "en",
    tagline = SEO_TAGLINE,
    defaultLanguage = "en",
}: {
    title: string;
    description?: string;
    path?: string;
    image?: string;
    keywords?: string[];
    lang?: Language;
    tagline?: string;
    defaultLanguage?: Language;
}): Metadata {
    return buildSegmentPageMetadata({
        lang,
        path,
        segmentTitle: title,
        description,
        keywords,
    });
}

export function buildOgImageUrl(lang: Language = DEFAULT_LANGUAGE): string {
    return buildAbsoluteUrl(buildStaticShareImagePath(lang));
}
